/**
 * Live account consent preference sync (privacy page + shared toggles).
 */
import type { ApiClient } from '../../shared/api/ApiClient.js';
import { readStoredAnalyticsSessionIds } from '../../shared/analytics/analyticsSessionStorage.js';
import { publishCustomerConsentUpdated } from '../../shared/crossTab/customerCrossTab.js';
import { customerLogger } from '../../shared/logging/customerLogger.js';
import { buildAcceptSelection, type ConsentDecision } from './consentTypes.js';
import { readConsent, writeConsent } from './consentStorage.js';
import { syncAnalyticsConsentRevoke } from './consentRevokeSync.js';
import {
  readAnonymousKeyForMarketingSave,
  saveMarketingAnalyticsConsentPreferences,
} from './marketingAnalyticsConsentSave.js';
import type { ConsentApiRolloutFallback } from './platformConsentApi.js';
import { resolveConsentStorageTenantCode } from './consentStorageTenant.js';

export interface AccountConsentPreferenceValues {
  readonly analytics: boolean;
  readonly marketing: boolean;
}

export async function syncAccountConsentPreferences(
  api: ApiClient,
  input: {
    readonly tenantCode: string;
    readonly authToken: string | null;
    readonly values: AccountConsentPreferenceValues;
    readonly loyaltyProfilingAvailable?: boolean;
    readonly rollout?: ConsentApiRolloutFallback;
  },
): Promise<void> {
  const {
    tenantCode,
    authToken,
    values,
    loyaltyProfilingAvailable = false,
    rollout,
  } = input;
  const storageTenant = resolveConsentStorageTenantCode(tenantCode);
  const storageOptions = { loyaltyProfilingAvailable };
  const storage =
    typeof window === 'undefined' ? null : window.localStorage;
  const anonymousKey = readAnonymousKeyForMarketingSave(storageTenant, values.analytics);
  const sessionIds = values.analytics
    ? []
    : [...readStoredAnalyticsSessionIds(tenantCode)];

  const cookieDecision = ((): ConsentDecision => {
    const current = readConsent(storageTenant, storage, storageOptions);
    if (current !== null) {
      return {
        ...current,
        analytics: values.analytics,
        marketing: values.marketing,
        decidedAtMs: Date.now(),
      };
    }
    return buildAcceptSelection(
      { functional: false, analytics: values.analytics, marketing: values.marketing },
      Date.now(),
    );
  })();

  if (!values.analytics) {
    await syncAnalyticsConsentRevoke({
      tenantCode,
      api,
      decision: cookieDecision,
      anonymousKey,
      sessionIds,
      authToken,
      signedIn: true,
      ...(rollout !== undefined ? { rollout } : {}),
    });
  } else {
    try {
      await saveMarketingAnalyticsConsentPreferences(
        {
          analytics: values.analytics,
          marketing: values.marketing,
        },
        rollout !== undefined ? { rollout } : undefined,
      );
    } catch (err: unknown) {
      customerLogger.warn('account consent preferences sync failed', err);
      throw err;
    }
  }

  writeConsent(storageTenant, cookieDecision, storage, storageOptions);
  publishCustomerConsentUpdated(storageTenant, { scope: 'global' });
}
